import { Check, ChevronDown, Copy, Pin, PinOff } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import useAlert from 'src/hooks/useAlert';

import { copyTextToClipboard } from '../../helpers';
import styles from './TeamSectionGroup.module.scss';

const TeamSectionGroup = ({
  team,
  label,
  icon = null,
  openItems = [],
  completedItems = [],
  openCount = 0,
  completedCount = 0,
  totalCount = 0,
  expanded = true,
  onToggleExpanded,
  renderItems,
  getCopyText,
  isDefault = false,
  onToggleDefault,
}) => {
  const [showCompleted, setShowCompleted] = React.useState(false);
  const { showAlert, AlertType } = useAlert();

  if (totalCount === 0) return null;

  const completionPercent = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;

  const handleCopy = async (event) => {
    event.stopPropagation();
    const text = getCopyText?.();
    if (!text) return;

    try {
      const didCopy = await copyTextToClipboard(text);
      if (!didCopy) throw new Error('Copy failed');
      showAlert(AlertType.Success, '', `${label} tasks copied for Slack.`);
    } catch (error) {
      showAlert(AlertType.Error, '', 'Unable to copy tasks.');
    }
  };

  return (
    <div className={`${styles.teamGroup} ${isDefault ? styles.teamGroupDefault : ''}`}>
      <div className={`${styles.teamHeader} ${isDefault ? styles.teamHeaderDefault : ''}`}>
        <button
          type="button"
          className={styles.teamHeaderTrigger}
          onClick={() => onToggleExpanded?.(team)}
          aria-expanded={expanded}
        >
          <ChevronDown
            size={14}
            strokeWidth={2}
            className={`${styles.teamChevron} ${!expanded ? styles.teamChevronCollapsed : ''}`}
          />
          {icon ? (
            <span className={styles.teamIcon} aria-hidden="true">
              {icon}
            </span>
          ) : null}
          <span className={styles.teamLabel}>{label}</span>
          {isDefault ? <span className={styles.defaultBadge}>Your team</span> : null}
          <span className={styles.teamRollup}>
            {openCount > 0 ? (
              <span className={styles.rollupOpen}>{`${openCount} open`}</span>
            ) : null}
            {openCount > 0 && completedCount > 0 ? (
              <span className={styles.rollupDot} aria-hidden="true">
                ·
              </span>
            ) : null}
            {completedCount > 0 ? (
              <span className={styles.rollupDone}>
                <Check size={11} strokeWidth={2.5} aria-hidden="true" />
                {`${completedCount} done`}
              </span>
            ) : null}
          </span>
        </button>
        {onToggleDefault ? (
          <button
            type="button"
            className={styles.pinTeamButton}
            onClick={(event) => {
              event.stopPropagation();
              onToggleDefault(team);
            }}
            aria-pressed={isDefault}
            aria-label={isDefault ? 'Clear default team' : `Make ${label} my default team`}
            title={isDefault ? 'Clear default team' : `Make ${label} my default team`}
          >
            {isDefault ? (
              <PinOff size={13} strokeWidth={1.8} />
            ) : (
              <Pin size={13} strokeWidth={1.8} />
            )}
          </button>
        ) : null}
        {getCopyText ? (
          <button
            type="button"
            className={styles.copyTeamButton}
            onClick={handleCopy}
            aria-label={`Copy ${label} tasks for Slack`}
            title={`Copy ${label} tasks for Slack`}
          >
            <Copy size={13} strokeWidth={1.8} />
          </button>
        ) : null}
      </div>
      <div className={styles.teamProgressTrack}>
        <div
          className={`${styles.teamProgressFill} ${
            openCount === 0 ? styles.teamProgressDone : styles.teamProgressActive
          }`}
          style={{ width: `${completionPercent}%` }}
        />
      </div>
      {expanded ? (
        <div className={styles.teamBody}>
          {openItems.length > 0 ? (
            <div className={styles.teamTasksBox}>{renderItems(openItems)}</div>
          ) : (
            <div
              className={`${styles.teamEmpty} ${completedCount > 0 ? styles.teamEmptyDone : ''}`}
            >
              No open tasks.
            </div>
          )}
          {completedCount > 0 ? (
            <div className={styles.completedSection}>
              <button
                type="button"
                className={styles.completedToggle}
                onClick={() => setShowCompleted((prev) => !prev)}
                aria-expanded={showCompleted}
              >
                <ChevronDown
                  size={12}
                  strokeWidth={2}
                  className={`${styles.completedChevron} ${
                    !showCompleted ? styles.completedChevronCollapsed : ''
                  }`}
                />
                {`Completed (${completedCount})`}
              </button>
              {showCompleted ? (
                <div className={styles.teamTasksBox}>{renderItems(completedItems)}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

TeamSectionGroup.propTypes = {
  team: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
  openItems: PropTypes.array,
  completedItems: PropTypes.array,
  openCount: PropTypes.number,
  completedCount: PropTypes.number,
  totalCount: PropTypes.number,
  expanded: PropTypes.bool,
  onToggleExpanded: PropTypes.func,
  renderItems: PropTypes.func.isRequired,
  getCopyText: PropTypes.func,
  isDefault: PropTypes.bool,
  onToggleDefault: PropTypes.func,
};

export default TeamSectionGroup;
